import { IGeneratedCase } from '../../base/claim-card';
import * as dm from '../../../data-models';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useState } from 'react';
import {
  DateInput,
  DateInputProps,
  DateRangePicker,
} from '@progress/kendo-react-dateinputs';
import dayjs from 'dayjs';
import Button from '../../base/button';
import { enums } from '../../../utils';
import { Label } from '@progress/kendo-react-labels';

type FilterModalProps = {
  setFilterModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filterState: dm.ClaimCardFilterObject;
  setFilterState: React.Dispatch<
    React.SetStateAction<dm.ClaimCardFilterObject>
  >;
  setTotalFilterCount: React.Dispatch<React.SetStateAction<number>>;
  NTNs: IGeneratedCase[];
};

const trackerStagesList = ['Received', 'Review', 'Active', 'Closed'];

const getPresentLeaveReasons = (generatedCases: IGeneratedCase[]) => {
  const reasons: string[] = [];

  generatedCases.forEach((c) => {
    const reason = c.leaves?.[0]?.reason;
    if (reason && !reasons.includes(reason)) {
      reasons.push(reason);
    }
  });

  return reasons;
};

const initialiseTrackerStageCheckboxStates = (
  filterState: dm.ClaimCardFilterObject,
) => {
  const initialStates: Record<string, boolean> = {};

  for (const stage of trackerStagesList) {
    initialStates[stage] = filterState.trackerStages?.includes(stage) || false;
  }

  return initialStates;
};

const initialiseLeaveReasonCheckboxStates = (
  filterState: dm.ClaimCardFilterObject,
  cases: IGeneratedCase[] = [],
) => {
  const initialStates: Record<string, boolean> = {};

  const applicableReasons = getPresentLeaveReasons(cases);

  for (const reason of applicableReasons) {
    initialStates[reason] = filterState.leaveReasons?.includes(reason) || false;
  }

  return initialStates;
};

const FilterBadge = ({ count }: { count: number }) => {
  return <span>({count})</span>;
};

const StyledCheckbox = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => {
  return (
    <div className="tw-flex tw-w-full tw-gap-2 tw-items-start">
      <Checkbox
        id={`checkbox-${label}`}
        checked={checked}
        style={{
          color: '#FF2E2E',
          backgroundColor: checked ? '#015294' : '#FFFFFF00',
          borderRadius: '4px',
          width: '20px',
          height: '20px',
          boxShadow: 'none',
          flexShrink: 0,
        }}
        onChange={(e) => {
          onChange(e.value);
        }}
      />
      <span className="tw-leading-6 tw-break-words tw-min-w-0">{label}</span>
    </div>
  );
};

const CustomStartDateInput = (props: DateInputProps) => {
  return (
    <Label className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-my-0">
      <span>Start Date</span>
      <DateInput {...props} label={undefined} className="!tw-w-full" />
    </Label>
  );
};

const CustomEndDateInput = (props: DateInputProps) => {
  return (
    <Label className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-my-0">
      <span>End Date</span>
      <DateInput {...props} label={undefined} className="!tw-w-full" />
    </Label>
  );
};

export const FilterModal = ({
  setFilterModalOpen,
  filterState,
  setFilterState,
  setTotalFilterCount,
  NTNs,
}: FilterModalProps) => {
  const initActiveStatusCount = (filterState: dm.ClaimCardFilterObject) => {
    return filterState.trackerStages?.length ?? 0;
  };

  const initActiveTypeCount = (filterState: dm.ClaimCardFilterObject) => {
    return filterState.leaveReasons?.length ?? 0;
  };

  const [trackerStageCheckboxStates, setTrackerStageCheckboxStates] = useState(
    initialiseTrackerStageCheckboxStates(filterState),
  );

  const [minStartDate, setMinStartDate] = useState<string | null>(
    filterState.minStartDate ?? null,
  );
  const [maxEndDate, setMaxEndDate] = useState<string | null>(
    filterState.maxEndDate ?? null,
  );

  const [leaveReasonCheckboxStates, setLeaveReasonCheckboxStates] = useState(
    initialiseLeaveReasonCheckboxStates(filterState, NTNs),
  );

  const [activeStatusCount, setActiveStatusCount] = useState(
    initActiveStatusCount(filterState),
  );

  const [activeTypeCount, setActiveTypeCount] = useState(
    initActiveTypeCount(filterState),
  );

  const updateLeaveReasonState = (reason: string, checked: boolean) => {
    setLeaveReasonCheckboxStates((prev) => ({
      ...prev,
      [reason]: checked,
    }));
    setActiveTypeCount((prev) => (checked ? prev + 1 : prev - 1));
  };

  const updateTrackerStageState = (stage: string, checked: boolean) => {
    setTrackerStageCheckboxStates((prev) => ({
      ...prev,
      [stage]: checked,
    }));
    setActiveStatusCount((prev) => (checked ? prev + 1 : prev - 1));
  };

  const handleClearFilters = () => {
    setTrackerStageCheckboxStates(
      Object.fromEntries(
        Object.keys(trackerStageCheckboxStates).map((k) => [k, false]),
      ),
    );
    setActiveStatusCount(0);
    setActiveTypeCount(0);
    setLeaveReasonCheckboxStates(
      Object.fromEntries(
        Object.keys(leaveReasonCheckboxStates).map((k) => [k, false]),
      ),
    );
    setMinStartDate(null);
    setMaxEndDate(null);
  };

  return (
    <div className="tw-absolute tw-inset-x-0 tw-bg-white tw-shadow-lg tw-rounded-lg tw-p-6 tw-z-[2000]">
      <div
        className="tw-absolute tw-left-12 -tw-top-2 tw-w-0 tw-h-0"
        style={{
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderBottom: '10px solid white',
          filter: 'drop-shadow(0 -2px 2px rgba(0,0,0,0.08))',
        }}
      />
      <div className="tw-flex tw-w-full tw-justify-between tw-items-center tw-mb-3">
        <p className="tw-text-2xl tw-text-unm-adhoc2 tw-mb-0">Filters</p>
        <button
          id="filter-modal-close-button"
          className="tw-flex tw-items-center tw-text-unm-primary02-400 tw-font-semibold tw-p-0 tw-bg-transparent tw-border-none"
          onClick={() => setFilterModalOpen(false)}
        >
          <p className="tw-m-0 -tw-translate-y-[1px] tw-pr-2">x</p>
          <p className="tw-m-0">Close</p>
        </button>
      </div>
      <div className="tw-grid md:tw-grid-cols-3 tw-auto-cols-fr tw-justify-between tw-mb-4 tw-gap-5 md:tw-gap-10">
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
          <span className="tw-text-base tw-text-unm-secondary01-700">
            Status {<FilterBadge count={activeStatusCount} />}
          </span>
          <div className="tw-w-full tw-h-px tw-bg-unm-secondary01-700 tw-unm-secondary01-700" />
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
            {Object.keys(trackerStageCheckboxStates).map((stage) => (
              <StyledCheckbox
                key={stage}
                label={stage}
                checked={trackerStageCheckboxStates[stage]}
                onChange={(checked: boolean) => {
                  updateTrackerStageState(stage, checked);
                }}
              />
            ))}
          </div>
        </div>
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
          <span className="tw-text-base tw-text-unm-secondary01-700">
            Date range
          </span>
          <div className="tw-w-full tw-h-px tw-bg-unm-secondary01-700 tw-unm-secondary01-700" />
          <div className="tw-w-full">
            <DateRangePicker
              className="!tw-flex-col [&_.k-label]:tw-pb-3 [&_.k-label]:tw-flex [&_.k-label]:tw-flex-col [&_.k-label]:tw-gap-1 [&_.k-label]:tw-items-start tw-items-start "
              startDateInput={CustomStartDateInput}
              endDateInput={CustomEndDateInput}
              value={{
                start: minStartDate ? dayjs(minStartDate).toDate() : null,
                end: maxEndDate ? dayjs(maxEndDate).toDate() : null,
              }}
              onChange={(e) => {
                setMinStartDate(
                  e.value.start
                    ? dayjs(e.value.start).format('YYYY-MM-DD')
                    : null,
                );
                setMaxEndDate(
                  e.value.end ? dayjs(e.value.end).format('YYYY-MM-DD') : null,
                );
              }}
            />
          </div>
        </div>
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
          <span className="tw-text-base tw-text-unm-secondary01-700">
            Type {<FilterBadge count={activeTypeCount} />}
          </span>
          <div className="tw-w-full tw-h-px tw-bg-unm-secondary01-700 tw-unm-secondary01-700" />
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
            {Object.keys(leaveReasonCheckboxStates).map((reason) => (
              <StyledCheckbox
                key={reason}
                label={reason}
                checked={leaveReasonCheckboxStates[reason]}
                onChange={(checked: boolean) => {
                  updateLeaveReasonState(reason, checked);
                }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="tw-flex tw-justify-end tw-gap-4 tw-pt-6">
        <Button
          id="filter-modal-clear-selection-button"
          buttonStyle={enums.ButtonTypes.secondary}
          onClick={handleClearFilters}
        >
          Clear Selection
        </Button>
        <Button
          id="filter-modal-apply-and-save-button"
          onClick={() => {
            const newFilterState = {
              minStartDate: minStartDate ?? undefined,
              maxEndDate: maxEndDate ?? undefined,
              trackerStages: Object.keys(trackerStageCheckboxStates).filter(
                (stage) => trackerStageCheckboxStates[stage],
              ),
              leaveReasons: Object.keys(leaveReasonCheckboxStates).filter(
                (reason) => leaveReasonCheckboxStates[reason],
              ),
            };
            setFilterState(newFilterState);
            setTotalFilterCount(
              activeStatusCount +
                activeTypeCount +
                (minStartDate || maxEndDate ? 1 : 0),
            );
            setFilterModalOpen(false);
          }}
        >
          Apply and Save
        </Button>
      </div>
    </div>
  );
};
